import { useState } from 'react';
import type { TimelineSlot } from '@status/shared';
import { slotHasData, slotTooltip, statusVisual } from '../lib/format';

interface TimelineProps {
  slots: readonly TimelineSlot[];
  label: string;
}

/**
 * 可用性时间轴。每格一个时间桶，颜色取桶内最差状态。
 * 灰格表示该时段没有探测数据（服务未启动 / 刚部署），不等于故障。
 */
export function Timeline({ slots, label }: TimelineProps): React.JSX.Element {
  // 自绘 tooltip 而不是用原生 title：后者有约 1s 延迟，
  // 快速划过时根本不弹，手感上像是没做 hover。
  const [active, setActive] = useState<number | null>(null);

  const first = slots[0];
  const last = slots[slots.length - 1];
  const activeSlot = active === null ? undefined : slots[active];

  return (
    <div className="space-y-1">
      <div className="relative">
        <div
          className="flex items-end gap-[2px]"
          role="img"
          aria-label={`${label} 可用性时间轴，共 ${slots.length} 个时段`}
          onMouseLeave={() => setActive(null)}
        >
          {slots.map((slot, i) => {
            const hasData = slotHasData(slot);
            const visual = statusVisual(slot.status, hasData);
            const isActive = active === i;

            return (
              <div
                key={slot.timestamp}
                onMouseEnter={() => setActive(i)}
                className={`h-8 min-w-0 flex-1 cursor-pointer rounded-[2px] ${visual.swatch} ${
                  hasData ? '' : 'opacity-40'
                } ${
                  // 用描边 + 提亮而不是改透明度：色块本身就有颜色，
                  // 调透明度在深色背景下几乎看不出变化。
                  isActive
                    ? 'brightness-125 ring-2 ring-slate-900/40 dark:ring-white/70'
                    : ''
                }`}
              />
            );
          })}
        </div>

        {activeSlot !== undefined && (
          <div
            role="tooltip"
            className="pointer-events-none absolute -top-1 left-1/2 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
          >
            {slotTooltip(activeSlot)}
          </div>
        )}
      </div>

      <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500">
        <span>{first?.time ?? ''}</span>
        <span>{last?.time ?? ''}</span>
      </div>
    </div>
  );
}
